
<template>
  <v-dialog
    :value="value"
    :persistent="loading || persistent"
    :max-width="maxWidth"
    :overlay-opacity="0.6"
    transition="primary-confirm-transition"
    content-class="primary-confirm-dialog elevation-12"
    @input="onInput"
  >
    <v-card
      class="primary-confirm-dialog_inner"
      flat
    >
      <div class="primary-confirm-dialog_title">
        <slot name="title">
          {{ title }}
        </slot>
      </div>
      <div
        v-if="subtitle || $scopedSlots.subtitle"
        class="primary-confirm-dialog_source"
      >
        <slot name="subtitle">
          {{ subtitle }}
        </slot>
      </div>

      <!-- Произвольное тело между текстом и кнопками: форма, алерт и т.п. -->
      <div
        v-if="$scopedSlots.body"
        class="primary-confirm-dialog_body"
      >
        <slot name="body" />
      </div>

      <div
        class="primary-confirm-dialog_actions"
        :class="{
          'primary-confirm-dialog_actions__single': hideConfirmBtn,
          'primary-confirm-dialog_actions__triple': !!$scopedSlots['extra-btn'],
        }"
      >
        <slot name="cancel-btn">
          <!-- color: @color-secondary из colors.less, в шаблон переменная не пробрасывается -->
          <large-btn
            class="primary-confirm-dialog_btn primary-confirm-dialog_btn__cancel"
            color="#f1f3f6"
            height="64px"
            :uppercase="false"
            :disabled="loading"
            block
            @click="close"
          >
            {{ cancelText }}
          </large-btn>
        </slot>
        <slot name="confirm-btn">
          <large-btn
            v-if="!hideConfirmBtn"
            :color="confirmBtnColor"
            class="primary-confirm-dialog_btn primary-confirm-dialog_btn__confirm"
            height="64px"
            :uppercase="false"
            :loading="loading"
            :disabled="confirmDisabled"
            block
            @click="$emit('confirm')"
          >
            {{ confirmText }}
          </large-btn>
        </slot>
        <slot name="extra-btn" />
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import LargeBtn from '@/components/ui/buttons/LargeBtn.vue'

  export default {
    name: 'PrimaryConfirmDialog',
    emits: ['confirm', 'cancel', 'close', 'input'],
    components: {
      LargeBtn,
    },
    props: {
      value: {
        type: Boolean,
        default: true,
      },
      title: String,
      subtitle: {
        type: String,
        default: null,
      },
      icon: {
        type: String,
        default: null,
      },
      confirmText: {
        type: String,
        default: 'Подтвердить',
      },
      cancelText: {
        type: String,
        default: 'Отмена',
      },
      maxWidth: {
        type: String,
        default: '560px',
      },
      loading: {
        type: Boolean,
        default: false,
      },
      persistent: {
        type: Boolean,
        default: true,
      },
      closeAfterLoading: {
        type: Boolean,
        default: false,
      },
      hideConfirmBtn: {
        type: Boolean,
        default: false,
      },
      confirmBtnColor: {
        type: String,
        default: 'primary',
      },
      // Подтверждение недоступно, пока форма в теле не валидна
      confirmDisabled: {
        type: Boolean,
        default: false,
      },
    },
    watch: {
      loading (val, oldVal) {
        if (oldVal && !val && this.closeAfterLoading) {
          this.close()
        }
      },
    },
    methods: {
      // v-dialog шлёт input(false) по клику вне окна и по Esc:
      // раньше это мутировало проп value внутри компонента, родитель об этом не знал
      onInput (val) {
        if (!val) {
          this.close()
        }
      },
      close () {
        this.$emit('input', false)
        this.$emit('close')
        this.$emit('cancel')
      },
    },
  }
</script>

<style lang="less">
// Появление: короче дефолтного dialog-transition (0.3s, scale 0.5) и без отскока
.v-dialog.primary-confirm-dialog {
  border-radius: 16px;
  transition: opacity 0.2s ease-out, transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.primary-confirm-transition-enter,
.primary-confirm-transition-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}

.primary-confirm-dialog {
  .v-card.primary-confirm-dialog_inner {
    padding: 40px 32px 32px;
    border-radius: 16px;

    // На телефоне (375px) десктопные 32px по бокам съедали почти пятую часть окна
    @media (max-width: 600px) {
      padding: 28px 20px 20px;
    }
  }

  &_title {
    font-size: 26px;
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: -0.01em;
    text-align: center;
    text-wrap: balance;
    color: #111;
  }

  // Факт, который влияет на решение: серый, ключевое слово (<b>) чёрное
  &_source {
    margin-top: 12px;
    font-size: 18px;
    font-weight: 400;
    line-height: 1.4;
    text-align: center;
    text-wrap: balance;
    color: #6b6b6b;

    b,
    strong {
      font-weight: 500;
      color: #111;
    }
  }

  // Тело (форма/алерт) - по левому краю, в отличие от центрированных текстов
  &_body {
    margin-top: 24px;
    text-align: left;
  }

  // «Отмена» уже, чем подтверждение: пропорция сама показывает ожидаемое действие
  &_actions {
    display: grid;
    grid-template-columns: 2fr 3fr;
    gap: 12px;
    margin-top: 50px;

    &__single {
      grid-template-columns: 1fr;
    }

    // Три кнопки в один ряд: «Отмена» уже, оба действия равные (как в макете)
    // Жёсткие размеры кнопок независимо от фактической ширины диалога:
    // отмена 120px, действия по 200px; 120 + 200 + 200 + 2 gap = 544 контента,
    // с паддингами карточки 32+32 диалогу нужно 608px
    &__triple {
      grid-template-columns: 120px 200px 200px;
      justify-content: center;

      // Колонки жёсткие, а v-btn не переносит текст: длинная подпись вылезала за окно
      // и давала горизонтальную прокрутку. В ряду из трёх подпись переносится на две строки
      .v-btn.large-btn.primary-confirm-dialog_btn {
        padding: 0 12px;
        white-space: normal;

        .v-btn__content {
          flex-wrap: wrap;
          line-height: 1.2;
        }
      }
    }

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
      margin-top: 28px;
    }
  }

  // LargeBtn задаёт 15px и тень для визарда; в диалоге кнопки плоские и крупнее
  .v-btn.large-btn.primary-confirm-dialog_btn {
    font-size: 17px;
    font-weight: 500;
    letter-spacing: normal;
    border-radius: 10px;
    padding: 0 20px;
    box-shadow: none !important;
  }

  // Цветные кнопки действий (hex-цвет мимо темы Vuetify): текст всегда белый.
  // Специфичность выше, чем у .theme--light.v-btn
  .v-btn.large-btn.primary-confirm-dialog_btn__confirm,
  .v-btn.large-btn.primary-confirm-dialog_btn__extra {
    color: #fff;
  }

  &_btn {
    &__confirm {
      svg {
        height: 28px;
        width: 40px;
      }
    }
  }
}
</style>
